import { previewPerimeterThreshold, priceScenario } from './analytics.js';
/**
 * The design the rule would produce at `threshold`, applied to the design as it stands.
 *
 * A HAND-BUILT MIRROR OF `thresholdWrites`, and the one thing in this file that can drift. The
 * command path builds database writes from a `CommandSlice`; this builds a `ScenarioDesign` from a
 * `SeparationState`, and neither shape can be expressed in the other's terms without dragging the
 * whole write layer into a pure engine. So the two are pinned by a test that runs the real handler
 * and folds its writes, rather than by a shared function.
 *
 * THE STRANDED PLAN IS THE SUBTLE PART, and it moves in one direction only. `bucketPatch` clears
 * `strandedAction`/`strandedMonth` when somebody enters the carve-out — a person inside the
 * perimeter strands nothing, so their mitigation plan is meaningless and is dropped. Releasing
 * somebody back to undecided does NOT drop it: the plan survives, and the person becomes a carrier
 * again the moment they are Remain-side. A sweep that forgot either half would misprice the
 * stranded residual, which is one of the four numbers it exists to show.
 */
export function designAtThreshold(state, threshold, 
/** The preview for this threshold, when a caller has already computed one — the sweep has. */
preview = previewPerimeterThreshold(state, threshold)) {
    const design = state.design;
    if (!preview.enterIds.length && !preview.exitIds.length)
        return design;
    const buckets = { ...design.buckets };
    const bucketMeta = { ...design.bucketMeta };
    const strandedPlan = { ...design.strandedPlan };
    for (const id of preview.enterIds) {
        buckets[id] = 'Carve-out';
        bucketMeta[id] = { src: 'rule', joinStage: 'perimeter' };
        // Entering the perimeter drops the plan, exactly as `bucketPatch` does.
        delete strandedPlan[id];
    }
    for (const id of preview.exitIds) {
        // Released to UNDECIDED, not to Remain: an absent row is what the rule is free to sweep again,
        // and an explicit 'Remain' would be a human decision this rule has no business writing.
        delete buckets[id];
        delete bucketMeta[id];
    }
    return { ...design, buckets, bucketMeta, strandedPlan };
}
/**
 * The whole-percent threshold a dedication is still inside, and the one where it drops out.
 *
 * THE PRODUCT'S RESOLUTION IS ONE PERCENTAGE POINT. The slider is `min=0 max=100 step=5` and the box
 * beside it rounds to a whole percent before dispatching `value / 100`, so 0.3751 is not a rule
 * anybody can store. Pricing raw dedication values shows points nobody can dial in.
 *
 * TWO STEPS PER VALUE, NOT ONE, and the second is the one the first draft missed. A cohort at 90%
 * is IN at 90% and OUT at 91% — the whole move happens across that single point, and a curve
 * carrying only the dedications would jump from 90% to the next dedication below, reporting a move
 * as if it were spread over a range the reader could pick anywhere in. Worse, above the highest
 * dedication there is no dedication at all, so the rule's largest act — releasing every pick it
 * owns — sat off the end of the table entirely: on a roster of forty people at 90%, a forty-person
 * move one slider click away was absent from the curve and invisible to the headline.
 *
 * FLOOR for the first, because the step has to be one where the person is still IN: rounding 0.4285
 * up to 0.43 names a threshold they have already left. The epsilon is for the float noise that makes
 * `0.29` arrive as `0.28999999999999998` — without it every percent-stated roster, which is nearly
 * all of them, would slide a whole point down.
 */
function dialableStep(dedication) {
    return Math.floor(dedication * 100 + 1e-9) / 100;
}
/** The next whole percent up, built through the integer so it stays exactly k/100. */
function nextStep(step) {
    return (Math.round(step * 100) + 1) / 100;
}
/**
 * Every threshold the rule could be set to that classifies this roster differently, ascending.
 *
 * ONE POINT PER EQUIVALENCE CLASS. Between two adjacent entries no settable threshold changes who
 * qualifies, so the table is complete without being a hundred rows of the same answer.
 *
 * THE DOMAIN IS THE ONE THE RULE HAS, and it is enforced here rather than assumed. The importer
 * clamps dedication into 0–1, but `setEmployeeField` does not — a person edited to `60` by somebody
 * typing sixty for sixty percent is a finite number the roster will hold. Left alone it became a
 * step of 60, a row reading "6000%", and a headline sentence about a threshold that cannot exist.
 * The two directions are not symmetric: above 1 a person qualifies at every settable threshold, so
 * their step is 1.00; below 0 they qualify at none, so they define no step at all.
 *
 * BOUNDED AT 101 BY CONSTRUCTION, because every member is a whole percent in [0, 1].
 */
export function thresholdSteps(state) {
    const values = new Set();
    for (const p of state.employees) {
        if (p.isLeaver)
            continue;
        const d = p.dedication;
        if (d == null || !Number.isFinite(d) || d < 0)
            continue;
        const step = Math.min(1, dialableStep(d));
        values.add(step);
        // The point this cohort drops out. There is none above 1.00 — the rule stops there.
        if (step < 1)
            values.add(nextStep(step));
    }
    return [...values].sort((a, b) => a - b);
}
/**
 * Price the rule at every step it could be set to, from the design as it stands.
 *
 * NO CEILING, because `thresholdSteps` is bounded at 101 by the product's own resolution. The cost
 * is one full re-price per step — three stage bridges, the one-off costs and the stranded timeline,
 * each a pass over the whole roster — which measures at ~4.4 ms per point over 5,000 people, so the
 * worst honest case is a little under half a second. That is a click, not a drag, and the pane asks
 * for it on open rather than on every keystroke.
 */
export function sweepPerimeterThreshold(state) {
    const current = state.design.perimeterThreshold;
    /*
      THE STORED RULE ALWAYS HAS A ROW, even when no dedication puts it on the curve.
  
      `isCurrent` is an exact match, and the rule is stored as `value / 100` from a box a person typed
      into — so a rule of 29% on a roster whose dedications are 40% and 80% matched nothing, and the
      pane, whose whole job is to show a reader where they stand, silently showed them nowhere. The
      threshold is a real classification whether or not a dedication happens to sit on it.
    */
    const steps = thresholdSteps(state);
    if (current !== null && current >= 0 && current <= 1 && !steps.includes(current)) {
        steps.push(current);
        steps.sort((a, b) => a - b);
    }
    const points = steps.map((threshold) => {
        // ONE preview per point, shared with the design it implies: computing it twice was a whole
        // extra pass over the roster for a number already in hand.
        const preview = previewPerimeterThreshold(state, threshold);
        return {
            threshold,
            enters: preview.enterIds.length,
            exits: preview.exitIds.length,
            isCurrent: current !== null && threshold === current,
            price: priceScenario(state, designAtThreshold(state, threshold, preview)),
        };
    });
    /*
      THE STEEPEST MOVE, by the money it carries rather than by the headcount.
  
      Headcount was the first axis and it is the wrong one: twenty juniors and twenty engineers move
      the same number of people and a very different amount of run-rate, and the second is what a
      committee is deciding about. Day-1 cost is the tie-breaker's own axis too, so two steps moving
      equal headcount no longer resolve by whichever came first in the roster.
    */
    let steepest = null;
    for (let i = 1; i < points.length; i++) {
        const a = points[i - 1];
        const b = points[i];
        const moved = Math.abs(b.price.costDay1 - a.price.costDay1);
        if (moved === 0 && b.price.hc.day1 === a.price.hc.day1)
            continue;
        if (steepest === null || moved > Math.abs(steepest.costDay1)) {
            steepest = {
                from: a.threshold,
                to: b.threshold,
                headcount: b.price.hc.day1 - a.price.hc.day1,
                costDay1: b.price.costDay1 - a.price.costDay1,
                oneOffTotal: b.price.oneOffTotal - a.price.oneOffTotal,
                strandedResidual: b.price.strandedResidual - a.price.strandedResidual,
            };
        }
    }
    return { points, current, steepest };
}
//# sourceMappingURL=sweep.js.map